import { TableCell } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Cell } from "@tanstack/react-table";
import { memo, useMemo } from "react";
import { useCellCopySelected } from "../hooks/useCellCopySelected";
import { useDataTableOptions } from "../hooks/useDataTableOptions";
import DataTableCellContent from "./DataTableCellContent";

interface DataTableCellProps<TData extends { id: string }> {
  cell: Cell<TData, unknown>;
  firstVisibleColumnId?: string;
}

const getAggregatedClassName = (depth: number) => {
  switch (depth) {
    case 0:
      return "bg-slate-300 font-semibold text-slate-800";
    case 1:
      return "bg-slate-200 font-semibold text-slate-800";
    case 2:
      return "bg-slate-100 font-semibold text-slate-800";
    default:
      return "";
  }
};

function DataTableCell<TData extends { id: string }>({
  cell,
  firstVisibleColumnId,
}: DataTableCellProps<TData>) {
  const options = useDataTableOptions();
  const isCopySelected = useCellCopySelected(cell.row.index, cell.column.id);
  const defaultClassName = "block truncate p-2 relative border-r h-8";
  const pinnedCellClassName = "sticky bg-slate-50 z-10";
  const sortedOrFilteredCellClassName = "bg-blue-50 text-blue-700";
  const copySelectedCellClassName =
    "bg-blue-100 outline outline-blue-300 outline-1";
  const aggregatedCellClassName = getAggregatedClassName(cell.row.depth);

  const isFilteredOrSorted = options.isFilteredOrSorted(cell.column.id);
  const isPinned = cell.column.getIsPinned();
  const isAggregated = cell.getIsAggregated();

  const style = useMemo(() => {
    return {
      left: `calc(var(--col-${cell.column.id}-position) * 1px)`,
      width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
    };
  }, [cell.column.id]);

  return (
    <TableCell
      className={cn(defaultClassName, {
        [pinnedCellClassName]: isPinned,
        [sortedOrFilteredCellClassName]: isFilteredOrSorted && !isAggregated,
        [aggregatedCellClassName]: isAggregated,
        [copySelectedCellClassName]: isCopySelected,
      })}
      style={style}
      data-column-id={cell.column.id}
      data-row-index={cell.row.index}
    >
      <DataTableCellContent
        cell={cell}
        isAggregated={isAggregated}
        firstVisibleColumnId={firstVisibleColumnId}
      />
      {cell.column.getIsResizing() ? (
        <div className="w-[1px] bg-blue-400 absolute top-0 right-0 bottom-0" />
      ) : null}
    </TableCell>
  );
}

export default memo(DataTableCell) as <TData extends { id: string }>(
  props: DataTableCellProps<TData>,
) => JSX.Element;
